import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from "@/components/ui/card";
import {
  MapPin, Users, TrendingUp, Calendar, Navigation, RefreshCw, BarChart3, Map, List
} from "lucide-react";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const CityDemandPage = () => {
  const [demandData, setDemandData] = useState(null);
  const [allVotes, setAllVotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("list");
  const [selectedCity, setSelectedCity] = useState(null);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersLayerRef = useRef(null);

  const fetchDemandData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/votes/demand-summary`);
      setDemandData(res.data);
    } catch (err) {
      console.error("Failed to fetch city demand:", err);
      toast.error("Failed to load city demand data");
    } finally {
      setLoading(false);
    }
  };

  const fetchAllVotesForCity = async (cityName) => {
    try {
      const res = await axios.get(`${API}/votes/city-votes/${encodeURIComponent(cityName)}`);
      setAllVotes(res.data.votes || []);
      setSelectedCity(cityName);
    } catch (err) {
      console.error("Failed to fetch votes for city:", err);
      toast.error("Failed to load vote locations");
    }
  };

  useEffect(() => {
    fetchDemandData();
  }, []);

  // Initialize/update map when view mode changes or votes change
  useEffect(() => {
    if (viewMode !== "map" || !mapRef.current) {
      return;
    }

    // Initialize map if not already done
    if (!mapInstanceRef.current) {
      mapInstanceRef.current = L.map(mapRef.current).setView([20.5937, 78.9629], 5);
      
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap contributors'
      }).addTo(mapInstanceRef.current);
      
      markersLayerRef.current = L.layerGroup().addTo(mapInstanceRef.current);
    }

    // Clear existing markers
    if (markersLayerRef.current) {
      markersLayerRef.current.clearLayers();
    }

    // Add markers for all votes
    if (allVotes.length > 0) {
      const bounds = [];
      allVotes.forEach((vote, index) => {
        if (vote.location?.lat && vote.location?.lng) {
          const marker = L.marker([vote.location.lat, vote.location.lng])
            .bindPopup(`
              <div style="min-width: 150px;">
                <strong>Vote #${index + 1}</strong><br/>
                <span style="font-size: 12px; color: #666;">${vote.location.address || 'No address'}</span><br/>
                <span style="font-size: 11px; color: #999;">${vote.created_at ? new Date(vote.created_at).toLocaleDateString() : ''}</span>
              </div>
            `);
          markersLayerRef.current.addLayer(marker);
          bounds.push([vote.location.lat, vote.location.lng]);
        }
      });

      // Fit bounds to show all markers
      if (bounds.length > 0) {
        mapInstanceRef.current.fitBounds(bounds, { padding: [50, 50] });
      }
    }
  }, [viewMode, allVotes]);

  // Cleanup map on unmount
  useEffect(() => {
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  const handleCityClick = (cityName) => {
    fetchAllVotesForCity(cityName);
    setViewMode("map");
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const filteredCities = demandData?.cities?.filter(city => 
    city.city_name?.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="city-demand-page">
      {/* Header Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Votes
            </CardTitle>
            <TrendingUp className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{demandData?.total_votes || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Customer interest signals
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Cities with Demand
            </CardTitle>
            <MapPin className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{demandData?.total_cities || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Potential expansion markets
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Top City
            </CardTitle>
            <BarChart3 className="h-4 w-4 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold truncate">
              {demandData?.cities?.[0]?.city_name || "-"}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {demandData?.cities?.[0]?.total_votes || 0} votes
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Search and Refresh */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Input
            placeholder="Search cities..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        </div>
        <div className="flex items-center gap-2">
          {/* View Mode Toggle */}
          <div className="flex items-center border rounded-lg overflow-hidden">
            <Button 
              variant={viewMode === "list" ? "default" : "ghost"} 
              size="sm"
              onClick={() => setViewMode("list")}
              className="rounded-none"
            >
              <List className="w-4 h-4 mr-1" />
              List
            </Button>
            <Button 
              variant={viewMode === "map" ? "default" : "ghost"} 
              size="sm"
              onClick={() => setViewMode("map")}
              className="rounded-none"
            >
              <Map className="w-4 h-4 mr-1" />
              Map
            </Button>
          </div>
          <Button variant="outline" onClick={fetchDemandData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Map View */}
      {viewMode === "map" && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Demand Locations Map</span>
              {selectedCity && (
                <span className="text-sm font-normal text-muted-foreground">
                  Showing {allVotes.length} locations for {selectedCity}
                </span>
              )}
            </CardTitle>
            <CardDescription>
              Click on a city in the table below to view all vote locations on the map.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div 
              ref={mapRef} 
              className="w-full h-[400px] rounded-xl border border-border overflow-hidden mb-4"
              style={{ zIndex: 1 }}
            />
            {!selectedCity && (
              <p className="text-center text-muted-foreground text-sm">
                Select a city from the table below to view all demand locations on the map
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Cities Table */}
      <Card>
        <CardHeader>
          <CardTitle>City Demand Rankings</CardTitle>
          <CardDescription>
            Areas where customers tried to book rides but no drivers were available.
            Use this data to prioritize expansion.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">#</TableHead>
                  <TableHead>City / Area</TableHead>
                  <TableHead className="text-center">Total Votes</TableHead>
                  <TableHead className="text-center">Unique Customers</TableHead>
                  <TableHead>First Interest</TableHead>
                  <TableHead>Latest Vote</TableHead>
                  <TableHead>Location</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCities.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                      {searchQuery ? "No cities match your search" : "No city demand data yet"}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredCities.map((city, index) => (
                    <TableRow key={city.city_name} className={selectedCity === city.city_name ? "bg-primary/5" : ""}>
                      <TableCell className="font-medium text-muted-foreground">
                        {index + 1}
                      </TableCell>
                      <TableCell>
                        <button 
                          onClick={() => handleCityClick(city.city_name)}
                          className="flex items-center gap-2 hover:text-primary transition-colors text-left"
                        >
                          <div className={`w-2 h-2 rounded-full ${
                            index === 0 ? 'bg-green-500' : 
                            index < 3 ? 'bg-amber-500' : 'bg-gray-300'
                          }`} />
                          <span className="font-medium hover:underline">{city.city_name}</span>
                          <Map className="w-3 h-3 text-muted-foreground" />
                        </button>
                      </TableCell>
                      <TableCell className="text-center">
                        <button
                          onClick={() => handleCityClick(city.city_name)}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary/10 text-primary font-semibold hover:bg-primary/20 transition-colors"
                        >
                          <TrendingUp className="w-3 h-3" />
                          {city.total_votes}
                        </button>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="inline-flex items-center gap-1 text-muted-foreground">
                          <Users className="w-3 h-3" />
                          {city.unique_customers_count}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        <span className="inline-flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(city.first_vote)}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDate(city.latest_vote)}
                      </TableCell>
                      <TableCell>
                        {city.sample_location?.lat ? (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  onClick={() => handleCityClick(city.city_name)}
                                  className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
                                >
                                  <Navigation className="w-3 h-3" />
                                  View All ({city.total_votes})
                                </button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Click to view all {city.total_votes} locations on map</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : (
                          <span className="text-xs text-muted-foreground">-</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CityDemandPage;
